import React from 'react';
import moment from 'moment/moment';
import {Tag} from 'antd';
import CreditCardDetails from '../../components/pages/CardsPage/CreditCardDetails';
import InvoiceStatusMark from '../../components/pages/InvoicesPage/InvoiceStatusMark';
import {
  AddInvoiceIcon,
  Invoice2Icon,
  InvoicePendingIcon,
  InvoiceSuggestIcon,
  TransactionDeclinedIcon,
  WithLessOutsideIcon,
} from '../../icons';
import {objectHelpers} from './object.helpers';
import {
  expenseTypesConstants,
  invoiceMatchTypeConstants,
  invoicePaymentStatusConstants,
  invoiceStatusConstants
} from '../../constants';
import routes from '../../routes/routes.json';

const gObjProp = objectHelpers.getObjProp;

const {MATCHED, PENDING} = invoiceStatusConstants;

const {AUTOMATED} = invoiceMatchTypeConstants;

const invoicePaymentStatuses = {
  [invoicePaymentStatusConstants.DUE]: {
    key: 'invoiceStatuses.due',
    color: '#FFCC00'
  },
  [invoicePaymentStatusConstants.OVERDUE]: {
    key: 'invoiceStatuses.overdue',
    color: '#FF4D4F'
  },
  [invoicePaymentStatusConstants.PAID]: {
    key: 'invoiceStatuses.paid',
    color: '#52C41A'
  },
  [invoicePaymentStatusConstants.PAID_OUTSIDE]: {
    key: 'invoiceStatuses.paid',
    color: '#52C41A'
  },
  [invoicePaymentStatusConstants.PROCESSING]: {
    key: 'invoiceStatuses.processing',
    color: '#CDD0D5'
  },
  [invoicePaymentStatusConstants.UPCOMING]: {
    key: 'invoiceStatuses.upcoming',
    color: '#CDD0D5'
  },
};


export const invoicesHelpers = {
  convertInvoiceToTransaction: (invoice) => {
    if (!invoice) return null;

    return {
      amount: invoice?.amount || 0,
      created_date: invoice?.date,
      source: invoice?.vendor
    }
  },
  convertMatchFilterPropsToApi: (input) => {
    const output = {};
    const dateFormat = 'YYYY-MM-DD';

    // Convert amountType
    if (input.amountType && input.amount) {
      const {amount, amountType} = input;
      const amountMapping = {
        '>': 'amount_lte',
        '<': 'amount_gte'
      };
      if (amountMapping[amountType]) {
        output[amountMapping[amountType]] = amount;
      } else if (amountType === '=') {
        output['amount_lte'] = amount;
        output['amount_gte'] = amount;
      }
    }

    // Convert dateRange
    if (input.dateRange && Array.isArray(input.dateRange) && input.dateRange.length === 2) {
      output.date_gte = input.dateRange[0].format(dateFormat);
      output.date_lte = input.dateRange[1].format(dateFormat);
    }

    // Add other input values as they are, excluding amountType and dateRange
    Object.keys(input).forEach(key => {
      if (!['amount', 'amountType', 'dateRange'].includes(key)) {
        output[key] = input[key];
      }
    });

    return output;
  },
  hasInvoiceParseError: (invoice) => {
    const threeDaysAgoDate = moment().subtract(3, 'days').startOf('day');

    if (gObjProp(invoice, 'has_parse_errors') === true) {
      return true;
    }

    if (gObjProp(invoice, 'status') === PENDING) {
      const createdAt = gObjProp(invoice, 'created_at');
      return moment(createdAt).startOf('day').isBefore(threeDaysAgoDate);
    }

    return false;
  },
  getInvoiceMatchType: (invoice) => {
    const matchType = gObjProp(invoice, 'match_type');
    const status = gObjProp(invoice, 'status');
    const hasParseErrors = invoicesHelpers.hasInvoiceParseError(invoice);

    if (hasParseErrors) {
      return <div className='invoice-match'><TransactionDeclinedIcon /></div>;
    }

    let matchElement;

    switch (status) {
      case MATCHED:
        matchElement = matchType === AUTOMATED ? <InvoiceSuggestIcon /> : <Invoice2Icon />;
        break;
      case PENDING:
        matchElement = <InvoicePendingIcon />;
        break;
      default:
        matchElement = matchType === AUTOMATED ? <InvoiceSuggestIcon /> : <AddInvoiceIcon />;
        break;
    }

    return <div className='invoice-match'>{matchElement}</div>;
  },
  getInvoicePaymentStatus: (status) => objectHelpers.getStatus(status, invoicePaymentStatuses),
  getInvoicePaymentType: (invoice) => {
    const cardMaskedPan = gObjProp(invoice, 'card_masked_pan') || '';
    const paymentStatus = gObjProp(invoice, 'payment_status');
    if (gObjProp(invoice, 'status') !== MATCHED) {
      return null;
    }

    if (paymentStatus === invoicePaymentStatusConstants.PAID_OUTSIDE) return <WithLessOutsideIcon />;

    if (cardMaskedPan) return <CreditCardDetails cardNumber={cardMaskedPan} />;

    return null;
  },
  getInvoiceStatusMark: (status) => objectHelpers.isNaV(status) ? '' : <InvoiceStatusMark statusCode={status} className='invoice-status-mark' />,

  getInvoiceSourceTypeTag: (value) => {
    return value
      ? (
        <Tag style={{
          backgroundColor: '#F3F3F4',
          borderColor: '#CDD0D5',
          borderRadius: 2,
          color: '#9CA1AA',
          marginRight: 0
        }}>{value}</Tag>
      )
      : '';
  },

  isInvoiceSettled: (status) => {
    return [
      invoicePaymentStatusConstants.PAID,
      invoicePaymentStatusConstants.PAID_OUTSIDE,
      invoicePaymentStatusConstants.PROCESSING
    ].includes(status);
  },

  isExpenseInvoiceType: (expense) => expense?.expense_type === expenseTypesConstants.INVOICE,

  isAccountingInvoice: (invoice) => invoice && invoice?.accounting_invoice_id,

  getInvoiceDetailsLink: (id) => {
    if (!id) return '#';
    return `${routes.invoicesList}/${id}`;
  }
}
